import type { EventDeviceRow } from '../types';
import type { AthleteCsvRow, DevicePackageAssignmentCsvRow } from '../types/EventsOverviewTypes';

export type { AthleteCsvRow, DevicePackageAssignmentCsvRow };

export const ATHLETES_CSV_HEADER = 'AthleteName,AthleteID,AthleteRaceNo';
export const DEVICE_PACKAGE_ASSIGNMENTS_CSV_HEADER =
  'AthleteName,AthleteID,AthleteRaceNo,RearDeviceID,FrontDeviceID';
const DEVICE_PACKAGE_ASSIGNMENTS_AUDIT_HEADERS = [
  'VerificationEditedAt',
  'VerificationEditedStage',
  'VerificationRearFrom',
  'VerificationRearTo',
  'VerificationFrontFrom',
  'VerificationFrontTo',
] as const;

function stripBom(s: string): string {
  if (s.charCodeAt(0) === 0xfeff) return s.slice(1);
  return s;
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      const next = line[i + 1];
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out;
}

function parseCsv(text: string): string[][] {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const rows: string[][] = [];
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) continue;
    rows.push(parseCsvLine(line));
  }
  return rows;
}

function normalizeHeader(s: string): string {
  return stripBom(s).trim().toLowerCase();
}

function csvEscape(v: string): string {
  const s = String(v ?? '');
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function parseWithHeaders(
  text: string,
  expectedHeaders: string[],
  mapRow: (cols: string[], header: string[], lineNo: number) => { ok: true } | { ok: false; error: string },
): { rows: string[][]; header: string[]; error?: string } {
  const parsed = parseCsv(text);
  if (parsed.length < 1) {
    return { rows: [], header: [], error: 'CSV is empty' };
  }

  const headerRaw = parsed[0].map((c) => c ?? '');
  const header = headerRaw.map(normalizeHeader).filter((h) => h.length > 0);
  const headerSet = new Set(header);

  if (header.length !== expectedHeaders.length) {
    return {
      rows: [],
      header: [],
      error: `CSV must have exactly ${expectedHeaders.length} columns: ${expectedHeaders.join(', ')}`,
    };
  }

  for (const h of expectedHeaders) {
    if (!headerSet.has(h)) {
      return {
        rows: [],
        header: [],
        error: `CSV headers must be: ${expectedHeaders.map((x) => x.replace(/^\w/, (c) => c.toUpperCase())).join(', ')}`,
      };
    }
  }

  for (let r = 1; r < parsed.length; r++) {
    const lineNo = r + 1;
    const cols = parsed[r] ?? [];
    const mapped = mapRow(cols, header, lineNo);
    if (!mapped.ok) {
      return { rows: [], header: [], error: mapped.error };
    }
  }

  return { rows: parsed, header, error: undefined };
}

/** Parse athletes.csv for upload / editor (three columns). */
export function parseAthletesCsv(text: string): { rows: AthleteCsvRow[]; error?: string } {
  const expected = ['athletename', 'athleteid', 'athleteraceno'];
  const parsed = parseCsv(text);

  if (parsed.length < 1) {
    return { rows: [], error: 'CSV is empty' };
  }

  const headerRaw = parsed[0].map((c) => c ?? '');
  const header = headerRaw.map(normalizeHeader).filter((h) => h.length > 0);
  const headerSet = new Set(header);

  if (header.length !== 3) {
    return {
      rows: [],
      error: 'CSV must have exactly 3 columns: AthleteName, AthleteID, AthleteRaceNo',
    };
  }

  for (const h of expected) {
    if (!headerSet.has(h)) {
      return {
        rows: [],
        error: 'CSV headers must be: AthleteName, AthleteID, AthleteRaceNo',
      };
    }
  }

  const idxName = header.indexOf('athletename');
  const idxAthleteId = header.indexOf('athleteid');
  const idxAthleteRaceNo = header.indexOf('athleteraceno');

  const out: AthleteCsvRow[] = [];
  const firstLineByAthleteId = new Map<string, number>();
  const firstLineByRaceNo = new Map<string, number>();

  for (let r = 1; r < parsed.length; r++) {
    const lineNo = r + 1;
    const cols = parsed[r] ?? [];
    const athleteName = (cols[idxName] ?? '').trim();
    const athleteId = (cols[idxAthleteId] ?? '').trim();
    const athleteRaceNo = (cols[idxAthleteRaceNo] ?? '').trim();

    if (!athleteName && !athleteId && !athleteRaceNo) continue;

    if (!athleteName || !athleteId || !athleteRaceNo) {
      return {
        rows: [],
        error: `Line ${lineNo}: each data row must have AthleteName, AthleteID, and AthleteRaceNo.`,
      };
    }

    if (firstLineByAthleteId.has(athleteId)) {
      const firstLine = firstLineByAthleteId.get(athleteId)!;
      return {
        rows: [],
        error: `Duplicate AthleteID "${athleteId}" on lines ${firstLine} and ${lineNo}.`,
      };
    }
    firstLineByAthleteId.set(athleteId, lineNo);

    if (firstLineByRaceNo.has(athleteRaceNo)) {
      const firstLine = firstLineByRaceNo.get(athleteRaceNo)!;
      return {
        rows: [],
        error: `Duplicate AthleteRaceNo "${athleteRaceNo}" on lines ${firstLine} and ${lineNo}.`,
      };
    }
    firstLineByRaceNo.set(athleteRaceNo, lineNo);

    out.push({ athleteName, athleteId, athleteRaceNo });
  }

  if (!out.length) {
    return { rows: [], error: 'No data rows found in CSV' };
  }

  return { rows: out };
}

/** Parse device-package-assignments.csv (required base columns; global device ID uniqueness). */
export function parseDevicePackageAssignmentsCsv(text: string): {
  rows: DevicePackageAssignmentCsvRow[];
  error?: string;
} {
  const expected = ['athletename', 'athleteid', 'athleteraceno', 'reardeviceid', 'frontdeviceid'];
  const parsed = parseCsv(text);

  if (parsed.length < 1) {
    return { rows: [], error: 'CSV is empty' };
  }

  const headerRaw = parsed[0].map((c) => c ?? '');
  const header = headerRaw.map(normalizeHeader).filter((h) => h.length > 0);
  const headerSet = new Set(header);

  if (header.length < 5) {
    return {
      rows: [],
      error: 'CSV must include at least: AthleteName, AthleteID, AthleteRaceNo, RearDeviceID, FrontDeviceID',
    };
  }

  for (const h of expected) {
    if (!headerSet.has(h)) {
      return {
        rows: [],
        error:
          'CSV headers must be: AthleteName, AthleteID, AthleteRaceNo, RearDeviceID, FrontDeviceID',
      };
    }
  }

  const idxName = header.indexOf('athletename');
  const idxAthleteId = header.indexOf('athleteid');
  const idxAthleteRaceNo = header.indexOf('athleteraceno');
  const idxRear = header.indexOf('reardeviceid');
  const idxFront = header.indexOf('frontdeviceid');
  const idxVerificationEditedAt = header.indexOf('verificationeditedat');
  const idxVerificationEditedStage = header.indexOf('verificationeditedstage');
  const idxVerificationRearFrom = header.indexOf('verificationrearfrom');
  const idxVerificationRearTo = header.indexOf('verificationrearto');
  const idxVerificationFrontFrom = header.indexOf('verificationfrontfrom');
  const idxVerificationFrontTo = header.indexOf('verificationfrontto');

  const out: DevicePackageAssignmentCsvRow[] = [];
  const firstLineByAthleteId = new Map<string, number>();
  const firstLineByRaceNo = new Map<string, number>();
  const deviceIdToLine = new Map<string, { lineNo: number; column: string }>();

  for (let r = 1; r < parsed.length; r++) {
    const lineNo = r + 1;
    const cols = parsed[r] ?? [];
    const athleteName = (cols[idxName] ?? '').trim();
    const athleteId = (cols[idxAthleteId] ?? '').trim();
    const athleteRaceNo = (cols[idxAthleteRaceNo] ?? '').trim();
    const rearDeviceId = (cols[idxRear] ?? '').trim();
    const frontDeviceId = (cols[idxFront] ?? '').trim();

    if (!athleteName && !athleteId && !athleteRaceNo && !rearDeviceId && !frontDeviceId) continue;

    if (!athleteName || !athleteId || !athleteRaceNo) {
      return {
        rows: [],
        error: `Line ${lineNo}: AthleteName, AthleteID, and AthleteRaceNo are required.`,
      };
    }

    if (firstLineByAthleteId.has(athleteId)) {
      const firstLine = firstLineByAthleteId.get(athleteId)!;
      return {
        rows: [],
        error: `Duplicate AthleteID "${athleteId}" on lines ${firstLine} and ${lineNo}.`,
      };
    }
    firstLineByAthleteId.set(athleteId, lineNo);

    if (firstLineByRaceNo.has(athleteRaceNo)) {
      const firstLine = firstLineByRaceNo.get(athleteRaceNo)!;
      return {
        rows: [],
        error: `Duplicate AthleteRaceNo "${athleteRaceNo}" on lines ${firstLine} and ${lineNo}.`,
      };
    }
    firstLineByRaceNo.set(athleteRaceNo, lineNo);

    for (const [deviceId, column] of [
      [rearDeviceId, 'RearDeviceID'],
      [frontDeviceId, 'FrontDeviceID'],
    ] as const) {
      if (!deviceId) continue;
      const prev = deviceIdToLine.get(deviceId);
      if (prev) {
        return {
          rows: [],
          error: `Duplicate device ID "${deviceId}" on line ${lineNo} (${column}) and line ${prev.lineNo} (${prev.column}). Device IDs must be unique across the file.`,
        };
      }
      deviceIdToLine.set(deviceId, { lineNo, column });
    }

    out.push({
      athleteName,
      athleteId,
      athleteRaceNo,
      rearDeviceId,
      frontDeviceId,
      verificationEditedAt: idxVerificationEditedAt >= 0 ? (cols[idxVerificationEditedAt] ?? '').trim() : '',
      verificationEditedStage:
        idxVerificationEditedStage >= 0 ? (cols[idxVerificationEditedStage] ?? '').trim() : '',
      verificationRearFrom: idxVerificationRearFrom >= 0 ? (cols[idxVerificationRearFrom] ?? '').trim() : '',
      verificationRearTo: idxVerificationRearTo >= 0 ? (cols[idxVerificationRearTo] ?? '').trim() : '',
      verificationFrontFrom:
        idxVerificationFrontFrom >= 0 ? (cols[idxVerificationFrontFrom] ?? '').trim() : '',
      verificationFrontTo: idxVerificationFrontTo >= 0 ? (cols[idxVerificationFrontTo] ?? '').trim() : '',
    });
  }

  if (!out.length) {
    return { rows: [], error: 'No data rows found in CSV' };
  }

  return { rows: out };
}

export function serializeAthletesCsv(rows: AthleteCsvRow[]): string {
  const lines = [ATHLETES_CSV_HEADER];
  for (const r of rows) {
    lines.push(
      [r.athleteName, r.athleteId, r.athleteRaceNo].map(csvEscape).join(','),
    );
  }
  return `${lines.join('\n')}\n`;
}

export function serializeDevicePackageAssignmentsCsv(rows: DevicePackageAssignmentCsvRow[]): string {
  const includeAuditCols = rows.some((r) =>
    [
      r.verificationEditedAt,
      r.verificationEditedStage,
      r.verificationRearFrom,
      r.verificationRearTo,
      r.verificationFrontFrom,
      r.verificationFrontTo,
    ].some((v) => String(v ?? '').trim() !== ''),
  );
  const header = includeAuditCols
    ? `${DEVICE_PACKAGE_ASSIGNMENTS_CSV_HEADER},${DEVICE_PACKAGE_ASSIGNMENTS_AUDIT_HEADERS.join(',')}`
    : DEVICE_PACKAGE_ASSIGNMENTS_CSV_HEADER;
  const lines = [header];
  for (const r of rows) {
    const baseValues = [r.athleteName, r.athleteId, r.athleteRaceNo, r.rearDeviceId, r.frontDeviceId];
    const auditValues = [
      r.verificationEditedAt ?? '',
      r.verificationEditedStage ?? '',
      r.verificationRearFrom ?? '',
      r.verificationRearTo ?? '',
      r.verificationFrontFrom ?? '',
      r.verificationFrontTo ?? '',
    ];
    lines.push(
      (includeAuditCols ? [...baseValues, ...auditValues] : baseValues).map(csvEscape).join(','),
    );
  }
  return `${lines.join('\n')}\n`;
}

/**
 * Parse device-package-assignments CSV into {@link EventDeviceRow} for terminal race display.
 * Emits one row per assigned device (rear and front).
 */
export function parseDevicePackageAssignmentsCsvToEventDeviceRows(
  csvText: string,
  raceId: string,
): EventDeviceRow[] {
  const rid = String(raceId ?? '').trim();
  if (!csvText?.trim() || !rid) return [];

  const { rows } = parseDevicePackageAssignmentsCsv(csvText);
  if (!rows.length) return [];

  const out: EventDeviceRow[] = [];
  const seenDevices = new Set<string>();

  for (const row of rows) {
    const athleteId = row.athleteId.trim();
    for (const deviceId of [row.rearDeviceId, row.frontDeviceId]) {
      const did = deviceId.trim();
      if (!did || seenDevices.has(did)) continue;
      seenDevices.add(did);
      out.push({
        deviceId: did,
        raceId: rid,
        assignedAthleteId: athleteId || undefined,
        assignedAthleteName: row.athleteName.trim() || undefined,
        assignedAthleteRaceNo: row.athleteRaceNo.trim() || undefined,
        assignedAt: undefined,
        assignedBy: undefined,
        recalled: undefined,
      });
    }
  }

  return out;
}

/** @deprecated Use parseAthletesCsv */
export const parseAthleteAssignmentsCsvEditor = parseAthletesCsv;

/** @deprecated Use parseDevicePackageAssignmentsCsvToEventDeviceRows */
export const parseAthleteAssignmentsCsvToEventDeviceRows = parseDevicePackageAssignmentsCsvToEventDeviceRows;

export type { AthleteAssignmentCsvRow } from '../types/EventsOverviewTypes';
